"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurCreativeDna() {
  const {
    ref: numeralRef,
    isVisible: numeralVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const paragraphs = [{
    text: "Ruchnii Studios was born from a desire to bridge the gap between professional cinematic quality and individual creative vision. Before we built our digital engine, we spent years studying the pacing, lighting, and emotional resonance of classic animation. We learned that a story is not just a sequence of frames, but a carefully curated journey that demands both technical precision and artistic soul.",
    dropCap: true
  }, {
    text: "Our platform is designed for the dreamers who have stories to tell but lacked the massive studio infrastructure to bring them to life. We believe that the tools of high-end animation should be accessible to everyone. By simplifying the complex workflows of industry giants, we empower you to focus on what truly matters: the narrative, the character, and the heart of your film.",
    dropCap: false
  }, {
    text: "Animation is often viewed as a technical hurdle, a mountain of software and rendering times. We see it as a canvas. Our mission is to remove the friction of production so that your imagination can flow directly onto the screen. We provide the structure, the assets, and the guidance; you provide the spark that turns a blank frame into a living, breathing world.",
    dropCap: false
  }, {
    text: "We operate with a commitment to quality that mirrors the legendary studios of the past. Every feature we release, every template we provide, and every tool we build is tested against the highest standards of cinematic storytelling. We don't just want to help you make a film; we want to help you make a film that resonates with audiences for generations.",
    dropCap: false
  }, {
    text: "The future of filmmaking is collaborative and decentralized. Ruchnii Studios is at the forefront of this movement, providing a space where independent creators can thrive. We are not interested in following industry trends; we are interested in setting them by giving you the power to create your own Disney and Pixar-inspired masterpieces from the comfort of your home.",
    dropCap: false
  }, {
    text: "If you are here, you are ready to start your journey. Our beta release is the first step toward a new era of independent animation. Join our waiting list, and let us help you transform your ideas into cinematic reality. The studio is open, the tools are ready, and your story is waiting to be told.",
    dropCap: false
  }];
  return <section data-section-id="1374" id="our-creative-dna" className="bg-background text-foreground py-24 md:py-44 px-5 md:px-16">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={imageRef} className="lg:sticky lg:top-24 transition-all duration-[900ms] ease-out" style={{
            opacity: imageVisible ? 1 : 0,
            transform: imageVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <div className="relative overflow-hidden group">
                <div className="aspect-[4/5] lg:aspect-[3/4] overflow-hidden">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/cinematic_workflow_detail.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/cinematic_workflow_detail.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/cinematic_workflow_detail.png-wvc-sizes" alt="Ruchnii Studios creative workspace showing digital animation tools and cinematic storyboarding" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" />
                </div>

                {}
                
              </div>

              {}
              <div className="pt-6 group/caption cursor-default">
                <p className="font-serif italic text-base md:text-lg text-foreground transition-colors duration-[250ms] group-hover/caption:text-primary">
                  Ruchnii Studios, Creative Core
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="h-px w-10 bg-primary" />
                  <p className="font-mono text-[11px] md:text-xs tracking-[0.18em] uppercase text-muted-foreground">
                    Production Lab &middot; Digital Frontier &middot; 2025
                  </p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            {}
            <div ref={numeralRef} className="flex items-center gap-4 mb-10 transition-all duration-[700ms] ease-out" style={{
            opacity: numeralVisible ? 1 : 0,
            transform: numeralVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <p className="font-mono text-xs md:text-[13px] tracking-[0.22em] uppercase text-primary">
                § N° 01 &nbsp;
              </p>
            </div>

            {}
            <div className="flex items-center gap-2 mb-12 transition-all duration-[700ms] ease-out delay-100" style={{
            opacity: numeralVisible ? 1 : 0
          }}>
              <div className="h-px w-[120px] bg-primary" />
              <div className="size-2 bg-primary" aria-hidden="true" />
            </div>

            {}
            <h2 ref={headlineRef} className="font-serif font-default font-normal text-foreground leading-[1.02] tracking-tight mb-16 md:mb-20 transition-all duration-[900ms] ease-out" style={{
            fontSize: "clamp(2.5rem, 5.4vw, 5rem)",
            letterSpacing: "-0.02em",
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              Democratizing the <em className="italic">dream</em>.
            </h2>

            {}
            <div className="max-w-[62ch]">
              {paragraphs.slice(0, 3).map((p, i) => <p key={i} data-index={i} className="font-sans text-base md:text-[17px] text-foreground/90 leading-[1.65] mb-8">
                  {p.dropCap && <span className={`font-serif float-left mr-2 leading-[0.85] block text-primary`} style={{
                fontSize: "clamp(4.5em, 6.5em, 6.5em)",
                marginTop: "0.05em"
              }}>
                      {p.text.charAt(0)}
                    </span>}
                  {p.dropCap ? p.text.slice(1) : p.text}
                </p>)}

              {}
              <div className="my-16 md:my-20">
                <div className="flex items-center gap-2 mb-8">
                  <div className="h-px flex-1 bg-primary/60" />
                  <div className="size-1.5 bg-primary" aria-hidden="true" />
                </div>

                <blockquote className="font-serif italic font-normal text-center leading-[1.25] tracking-tight px-4 md:px-8" style={{
                fontSize: "clamp(1.2rem, 1.8vw, 1.75rem)",
                color: "var(--primary)"
              }}>
                  "Create Your Own Disney and Pixar Films!"
                </blockquote>

                <div className="flex items-center gap-2 mt-8">
                  <div className="size-1.5 bg-primary" aria-hidden="true" />
                  <div className="h-px flex-1 bg-primary/60" />
                </div>
              </div>

              {}
              {paragraphs.slice(3).map((p, i) => <p key={i + 3} data-index={i + 3} className="font-sans text-base md:text-[17px] text-foreground/90 leading-[1.65] mb-8">
                  {p.text}
                </p>)}

              {}
              <div className="flex justify-center pt-8">
                <div className="size-2.5 bg-primary" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}